import React from 'react';
import { LearningMetric, ComponentProps, FeedbackSource } from '../types.js';

/**
 * LearningImpact - Platform Admin
 * 
 * RECOVERED FROM: /src/features/advanced/components/feedback/insights/components/LearningImpact.tsx
 * MODIFICATIONS FOR PLATFORM ADMIN:
 * - No changes needed - component is presentation-only
 * - Displays aggregated learning metrics across all tenants
 * - Shows ML model improvement over time
 * 
 * Following 00-READ-THIS-FIRST:
 * - SINGLE SOURCE OF TRUTH: Recovered original, not duplicated
 * - NO FUNCTIONAL DUPLICATES: This is the only LearningImpact
 */

interface LearningImpactProps extends ComponentProps {
  metrics: LearningMetric[];
  timeseriesData: Array<{
    timestamp: string;
    accuracy: number;
    confidence: number;
    patternStrength: number;
    source?: FeedbackSource;
  }>;
  source: FeedbackSource;
}

export const LearningImpact: React.FC<LearningImpactProps> = ({
  metrics,
  timeseriesData,
  source
}) => {
  const filteredMetrics = source === 'all'
    ? metrics
    : metrics.filter(m => m.source === source);

  const filteredTimeseries = source === 'all'
    ? timeseriesData
    : timeseriesData.filter(t => t.source === source);

  return (
    <div className="bg-gray-800/50 rounded-lg p-6">
      <h3 className="text-lg font-semibold mb-4">Learning Impact</h3>
      
      {/* ✅ Task 5: Show empty state when no data */}
      {filteredMetrics.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <p>No learning impact data available yet.</p>
          <p className="text-sm mt-2">Train models to see ML performance improvements.</p>
        </div>
      ) : (
        <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {filteredMetrics.map((metric, index) => (
          <div key={index} className="p-4 bg-gray-900/50 rounded-lg">
            <p className="text-sm text-gray-400">{metric.metricName}</p>
            <p className="text-2xl font-semibold mt-1">{metric.current}%</p>
            <div className="flex items-center mt-2">
              <span className={
                metric.trend === 'up' ? 'text-green-500' :
                metric.trend === 'down' ? 'text-red-500' :
                'text-gray-500'
              }>
                {metric.trend === 'up' ? '↑' : metric.trend === 'down' ? '↓' : '→'}
                {' '}
                {Math.abs(metric.current - metric.previous)}%
              </span>
              <span className="text-sm text-gray-400 ml-2">vs previous</span>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6">
        <h4 className="text-md font-semibold mb-3">Performance Trends</h4>
        <div className="bg-gray-900/50 rounded-lg p-4">
          <div className="grid grid-cols-1 gap-4">
            {filteredTimeseries.map((point, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm text-gray-400">{point.timestamp}</span>
                <div className="flex gap-4">
                  <span className="text-green-500">{point.accuracy}%</span>
                  <span className="text-blue-500">{point.confidence}%</span>
                  <span className="text-yellow-500">{point.patternStrength}%</span>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-4 text-xs text-gray-400">
            <span>Accuracy</span>
            <span>Confidence</span>
            <span>Pattern Strength</span>
          </div>
        </div>
      </div>
        </>
      )}
    </div>
  );
};
